import * as React from 'react';

export interface UseRotatingFadeOptions {
  count: number;
  fadeDurationMs?: number;
  rotateEveryMs: number | ((activeIndex: number) => number);
}

export interface UseRotatingFadeResult {
  activeIndex: number;
  isPaused: boolean;
  isVisible: boolean;
  reduceMotion: boolean;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
  showIndex: (nextIndex: number) => void;
}

export const useRotatingFade = (options: UseRotatingFadeOptions): UseRotatingFadeResult => {
  const { count, fadeDurationMs = 180, rotateEveryMs } = options;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(true);
  const [isPaused, setIsPaused] = React.useState(false);
  const [reduceMotion, setReduceMotion] = React.useState(false);
  const timeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(media.matches);

    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  const clearPendingTimeout = React.useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showIndex = React.useCallback(
    (nextIndex: number) => {
      clearPendingTimeout();

      if (reduceMotion) {
        setActiveIndex(nextIndex);
        setIsVisible(true);
        return;
      }

      setIsVisible(false);
      timeoutRef.current = window.setTimeout(() => {
        setActiveIndex(nextIndex);
        setIsVisible(true);
        timeoutRef.current = null;
      }, fadeDurationMs);
    },
    [clearPendingTimeout, fadeDurationMs, reduceMotion]
  );

  React.useEffect(() => {
    if (reduceMotion || isPaused || count < 2) {
      return;
    }

    const duration = typeof rotateEveryMs === 'function' ? rotateEveryMs(activeIndex) : rotateEveryMs;
    const rotationTimeout = window.setTimeout(() => {
      showIndex((activeIndex + 1) % count);
    }, duration);

    return () => {
      // Pausing rotation must let an in-progress fade finish revealing its next item.
      window.clearTimeout(rotationTimeout);
    };
  }, [activeIndex, count, isPaused, reduceMotion, rotateEveryMs, showIndex]);

  React.useEffect(() => clearPendingTimeout, [clearPendingTimeout]);

  return {
    activeIndex,
    isPaused,
    isVisible,
    reduceMotion,
    setIsPaused,
    showIndex
  };
};
